import {
  AltiusRes,
  CreateAiDealFormAnswer,
  CreateDealForm,
  CreateDealFormAnswer,
  CreateDealFormGroup,
  CreateDealFormQuestion,
  DealFolderForm,
  DealFormAnswer,
  DealFormData,
  DeleteDealFormGroup,
  DeleteDealFormQuestion,
  DuplicateDealForm,
  GetAnswersHistory,
  GetDealFormAnswer,
  GetDealFormData,
  IDeleteAnswerData,
  PublishDealFormAnswer,
  UpdateDealFormAnswer,
  UpdateDealFormGroup,
  UpdateDealFormQuestion,
  UpdateDealFormQuestionOrder,
  UpdateFormData
} from "store/types";
import { toFormData } from "utils";

import { DealAction } from "..";
import { AsyncActions, AsyncActionsParams } from "../base";

export interface IFormActions {
  getFormsAction: (dealId: number) => Promise<void>;
  createFormAction: (data: CreateDealForm) => Promise<DealFolderForm>;
  updateFormAction: (data: UpdateFormData) => Promise<DealFolderForm>;
  deleteFormAction: (data: { dealId?: number; formId: number }) => Promise<void>;
  duplicateFormAction: (data: DuplicateDealForm) => Promise<DealFolderForm>;
  aiAnswerFormAction: (data: CreateAiDealFormAnswer) => Promise<void>;

  getDealFormAction: (data: GetDealFormData) => Promise<DealFormData>;
  getDealFormAndStoreAction: (data: GetDealFormData) => Promise<void>;

  createFormGroupAction: (createFormGroupData: CreateDealFormGroup) => Promise<void>;
  updateFormGroupAction: (data: UpdateDealFormGroup) => Promise<void>;
  deleteFormGroupAction: (data: DeleteDealFormGroup) => Promise<void>;

  createFormQuestionAction: (data: CreateDealFormQuestion) => Promise<void>;
  updateFormQuestionAction: (data: UpdateDealFormQuestion, isOrder?: boolean) => Promise<void>;
  deleteFormQuestionAction: (data: DeleteDealFormQuestion) => Promise<void>;
  updateFormQuestionsOrderStoreAction: (data: UpdateDealFormQuestionOrder) => void;

  createFormAnswerAction: (data: CreateDealFormAnswer) => Promise<void>;
  publishFormAnswerAction: (data: PublishDealFormAnswer) => Promise<void>;

  getFormAnswersHistoryAction: (data: GetAnswersHistory) => Promise<DealFormAnswer[]>;
  clearDealFormAction: () => void;
  // uploadFileToQuestionAction: (data: IUploadFileData) => Promise<IFile>;
  setRefreshDealForm: (value: boolean) => void;

  clearFormAnswerAction: (data: PublishDealFormAnswer) => Promise<void>;
  getFormAnswerAction: (data: GetDealFormAnswer) => Promise<void>;
  updateFormAnswerAction: (answer: UpdateDealFormAnswer) => Promise<void>;
  deleteFormAnswerAction: (data: IDeleteAnswerData) => Promise<void>;
}

interface FormActionParams extends AsyncActionsParams {
  isSharedDeal: boolean;
}

export class FormActions extends AsyncActions implements IFormActions {
  protected readonly isSharedDeal: boolean;
  public constructor({ isSharedDeal, ...params }: FormActionParams) {
    super(params);
    this.isSharedDeal = isSharedDeal;
  }

  public getFormsAction: IFormActions["getFormsAction"] = async (dealId: number) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<Record<number, DealFolderForm>>>({
      url: this.isSharedDeal ? `/api/v0.0.3/shared-deals/${dealId}/forms` : `/api/v0.0.3/deals/${dealId}/forms`
    });
    this.dispatch({ type: DealAction.GetFormsSuccess, payload: data });
  };

  public createFormAction: IFormActions["createFormAction"] = async (data) => {
    const { dealId, sectionId, title } = data;
    const {
      data: { data: payload }
    } = await this.api.post<AltiusRes<DealFolderForm>>({
      url: dealId ? `/api/v0.0.3/deals/${dealId}/forms/new` : "/api/v0.0.3/global-workspace/forms",
      data: {
        section_id: sectionId,
        title
      }
    });
    this.dispatch({ type: DealAction.CreateFormSuccess, payload });
    return payload;
  };

  public updateFormAction: IFormActions["updateFormAction"] = async (data: UpdateFormData) => {
    const { formId, dealId, ...body } = data;

    const {
      data: { data: payload }
    } = await this.api.patch<AltiusRes<DealFolderForm>>({
      url: dealId ? `/api/v0.0.2/deals/${dealId}/forms/${formId}` : `/api/v0.0.3/global-workspace/forms/${formId}`,
      data: body
    });

    this.dispatch({ type: DealAction.UpdateFormSuccess, payload });
    return payload;
  };

  public deleteFormAction: IFormActions["deleteFormAction"] = async ({ dealId, formId }) => {
    await this.api.delete({
      url: dealId ? `/api/v0.0.2/deals/${dealId}/forms/${formId}` : `/api/v0.0.3/global-workspace/forms/${formId}`
    });
    this.dispatch({ type: DealAction.DeleteFormSuccess, payload: formId });
  };

  public duplicateFormAction: IFormActions["duplicateFormAction"] = async ({ formId, recurringDate }) => {
    const {
      data: { data: payload }
    } = await this.api.post<AltiusRes<DealFolderForm>>({
      url: `/api/v0.0.3/deals/forms/${formId}/duplicate`,
      data: recurringDate ? { recurring_date: recurringDate } : {}
    });
    this.dispatch({ type: DealAction.CreateFormSuccess, payload });
    return payload;
  };

  public aiAnswerFormAction: IFormActions["aiAnswerFormAction"] = async (data) => {
    const updatedData = { file_ids: data.file_ids, files: data.attachments };
    const payload = Object.fromEntries(Object.entries(updatedData).filter(([, value]) => value?.length));
    await this.api.post({
      url: `/api/v0.0.2/deals/forms/${data.formId}/ai-answer`,
      data: toFormData({
        ...payload,
        ...(data.question_id && { question_id: data.question_id }),
        ...(data.web_search && { web_search: data.web_search }),
        ...(data.dealId && { deal_id: data.dealId })
      }),
      headers: {
        "Content-Type": "multipart/form-data"
      }
    });
  };

  public getDealFormAction: IFormActions["getDealFormAction"] = async ({ dealId, formId, filter }) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<DealFormData>>({
      url: this.isSharedDeal
        ? `/api/v0.0.3/shared-deals/${dealId}/forms/${formId}`
        : `/api/v0.0.3/deals/forms/${formId}`,
      params: { filter }
    });
    return data;
  };

  public getDealFormAndStoreAction: IFormActions["getDealFormAndStoreAction"] = async (data): Promise<void> => {
    try {
      if (!data.disableLoading) {
        this.dispatch({ type: DealAction.GetDealFormRequest, payload: null });
      }
      const survey: DealFormData = await this.getDealFormAction(data);
      this.dispatch({ type: DealAction.GetDealFormSuccess, payload: survey });
    } catch (error) {
      this.dispatch({ type: DealAction.GetDealFormFailure, payload: null });
      throw error;
    }
  };

  // ====================================== GROUP ==========================================

  public createFormGroupAction: IFormActions["createFormGroupAction"] = async (createFormGroupData): Promise<void> => {
    const { dealId, formId, label, order, form_group_id, question_order } = createFormGroupData;
    const {
      data: { data: payload }
    } = await this.api.post<AltiusRes<DealFormData>>({
      url: `/api/v0.0.3/deals/forms/${formId}/groups`,
      data: { label, order, form_group_id, question_order, ...(dealId && { deal_id: dealId }) }
    });
    this.dispatch({ type: DealAction.GetDealFormSuccess, payload });
  };

  public updateFormGroupAction: IFormActions["updateFormGroupAction"] = async (data): Promise<void> => {
    const { formId, groupId, body } = data;
    const {
      data: { data: payload }
    } = await this.api.patch<AltiusRes<DealFormData>>({
      url: `/api/v0.0.3/deals/forms/${formId}/groups/${groupId}`,
      data: body
    });
    this.dispatch({ type: DealAction.GetDealFormSuccess, payload });
  };

  public deleteFormGroupAction: IFormActions["deleteFormGroupAction"] = async (data): Promise<void> => {
    const { formId, groupId } = data;
    const {
      data: { data: payload }
    } = await this.api.delete<AltiusRes<DealFormData>>({
      url: `/api/v0.0.3/deals/forms/${formId}/groups/${groupId}`
    });
    this.dispatch({ type: DealAction.GetDealFormSuccess, payload });
  };

  // ====================================== QUESTIONS ==========================================

  public createFormQuestionAction: IFormActions["createFormQuestionAction"] = async (data): Promise<void> => {
    const { formId, groupId, body } = data;
    const {
      data: { data: payload }
    } = await this.api.post<AltiusRes<DealFormData>>({
      url: `/api/v0.0.3/deals/forms/${formId}/groups/${groupId}/questions`,
      data: body
    });
    this.dispatch({ type: DealAction.GetDealFormSuccess, payload });
  };

  public createFormAnswerAction: IFormActions["createFormAnswerAction"] = async (data): Promise<void> => {
    const {
      data: { data: payload }
    } = await this.api.post<AltiusRes<DealFormAnswer>>({
      url: this.isSharedDeal
        ? `/api/v0.0.3/shared-deals/${data.dealId}/forms/${data.formId}/answer`
        : `/api/v0.0.2/deals/forms/${data.formId}/answer`,
      data: {
        answer: data.answer,
        question_uuid: data.questionUuid,
        section_id: data.sectionId,
        question_group_id: data.questionGroupId,
        ...(data.dealId && { deal_id: data.dealId })
      }
    });
    this.dispatch({ type: DealAction.SetDealFormDraft, payload });
  };

  public publishFormAnswerAction: IFormActions["publishFormAnswerAction"] = async (data): Promise<void> => {
    const { dealId, formId, questionId, sectionId, groupId } = data;
    const {
      data: { data: payload }
    } = await this.api.patch<AltiusRes<DealFormAnswer>>({
      url: this.isSharedDeal
        ? `/api/v0.0.3/shared-deals/${dealId}/forms/${formId}/publish-answer`
        : `/api/v0.0.2/deals/forms/${formId}/publish-answer`,
      data: {
        question_id: questionId,
        section_id: sectionId,
        question_group_id: groupId,
        ...(!this.isSharedDeal && dealId && { deal_id: dealId })
      }
    });
    this.dispatch({
      type: DealAction.PublishDealFormAnswerSuccess,
      payload
    });
  };

  public clearFormAnswerAction: IFormActions["clearFormAnswerAction"] = async (data): Promise<void> => {
    const { dealId, formId, questionId, sectionId, groupId } = data;
    const {
      data: { data: payload }
    } = await this.api.patch<AltiusRes<DealFormAnswer>>({
      url: this.isSharedDeal
        ? `/api/v0.0.3/shared-deals/${dealId}/forms/${formId}/clear-answer`
        : `/api/v0.0.2/deals/forms/${formId}/clear-answer`,
      data: {
        question_id: questionId,
        section_id: sectionId,
        question_group_id: groupId,
        ...(!this.isSharedDeal && dealId && { deal_id: dealId })
      }
    });
    this.dispatch({
      type: DealAction.ClearDealFormAnswerSuccess,
      payload
    });
  };

  public updateFormQuestionAction: IFormActions["updateFormQuestionAction"] = async (data, isOrder) => {
    const { formId, groupId, questionId, body } = data;

    const {
      data: { data: payload }
    } = await this.api.patch<AltiusRes<DealFormAnswer>>({
      url: `/api/v0.0.3/deals/forms/${formId}/groups/${groupId}/questions/${questionId}`,
      data: body
    });

    if (!isOrder) {
      this.dispatch({
        type: DealAction.UpdateDealFormQuestion,
        payload: {
          ...payload
        }
      });
    }
    // const payload: IUpdateQuestionResponse = response.data

    // this.dispatch({ type: DealAction.SetActiveForm, payload: response.data }) // set incoming form as active
    // this.dispatch({ type: DealAction.PatchFormSuccess, payload: response.data}) // update form in deal
  };

  public updateFormQuestionsOrderStoreAction: IFormActions["updateFormQuestionsOrderStoreAction"] = (data): void => {
    this.dispatch({
      type: DealAction.UpdateDealFormQuestionOrder,
      payload: data
    });
  };

  public deleteFormQuestionAction: IFormActions["deleteFormQuestionAction"] = async (data): Promise<void> => {
    const { formId, questionId, groupId } = data;
    const {
      data: { data: payload }
    } = await this.api.delete<AltiusRes<DealFormData>>({
      url: `/api/v0.0.3/deals/forms/${formId}/groups/${groupId}/questions/${questionId}`
    });
    this.dispatch({ type: DealAction.GetDealFormSuccess, payload });
  };

  public getFormAnswersHistoryAction: IFormActions["getFormAnswersHistoryAction"] = async ({
    dealId,
    formId,
    uuid
  }): Promise<DealFormAnswer[]> => {
    // const response: DealFormAnswerResponse[] = await new Promise((resolve) =>
    //   setTimeout(() => resolve(getAnswersHistoryResponse))
    // );
    const {
      data: { data: history }
    } = await this.api.get<AltiusRes<DealFormAnswer[]>>({
      url: this.isSharedDeal
        ? `/api/v0.0.3/shared-deals/${dealId}/forms/${formId}/question/${uuid}/history-answers`
        : `/api/v0.0.2/deals/forms/${formId}/question/${uuid}/history-answers`
    });
    return history;
  };

  public clearDealFormAction = (): void => {
    this.dispatch({ type: DealAction.ClearDealForm, payload: null });
  };

  public setRefreshDealForm: IFormActions["setRefreshDealForm"] = (value): void => {
    this.dispatch({
      type: DealAction.RequestRefreshDealFormSocket,
      payload: value
    });
  };

  public getFormAnswerAction: IFormActions["getFormAnswerAction"] = async ({ formId, answerId }) => {
    const {
      data: { data: payload }
    } = await this.api.get<AltiusRes<DealFormAnswer>>({
      url: `/api/v0.0.2/deals/forms/${formId}/answer/${answerId}`
    });

    this.dispatch({ type: DealAction.GetDealFormAnswerSuccess, payload });
  };

  public updateFormAnswerAction: IFormActions["updateFormAnswerAction"] = async ({
    dealId,
    formId,
    answerId,
    value,
    is_rtl,
    questionGroupId
  }) => {
    const {
      data: { data: payload }
    } = await this.api.patch<AltiusRes<DealFormAnswer>>({
      url: this.isSharedDeal
        ? `/api/v0.0.3/shared-deals/${dealId}/forms/${formId}/answer/${answerId}`
        : `/api/v0.0.2/deals/forms/${formId}/answer/${answerId}`,
      data: { answer: value, is_rtl, ...(dealId && { deal_id: dealId }) }
    });

    this.dispatch({
      type: DealAction.SetDealFormDraft,
      payload: { ...payload, question_group_id: questionGroupId }
    });
  };

  public deleteFormAnswerAction: IFormActions["deleteFormAnswerAction"] = async (
    data: IDeleteAnswerData
  ): Promise<void> => {
    const { answer, dealId } = data;
    await this.api.delete({
      url: this.isSharedDeal
        ? `/api/v0.0.3/shared-deals/${dealId}/forms/${answer.deal_form_id}/answer/${answer.id}`
        : `/api/v0.0.2/deals/forms/${answer.deal_form_id}/answer/${answer.id}`
    });
    this.dispatch({ type: DealAction.DiscardDealFormDraft, payload: answer });
  };
}
